import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { EmailIcon, PlusIcon, ProviderIcon, SendIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { sendCodeToPartners, sendEmailToCse } from '../../api/cse/cse'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import CseFilters from '../../components/pages/cse/cse-filters'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { billingTypeEnumMapping } from '../../lib/types/enums/cse/billing-type.enum'
import { ReductionTypeEnum } from '../../lib/types/enums/cse/reduction-type.enum'
import { CseFilter, CseTypes } from '../../lib/types/models/cse/cse'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformCseFiltersToUrl,
  transformUrlToCseFilters
} from '../../lib/utils/filters-mapping/cse'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const getCseInformation = (cseList: CseTypes.Cse[]) => {
  const emailSet = new Set<string>()
  const labelSet = new Set<string>()

  cseList.forEach((cse) => {
    if (cse.canReceiveEmails && cse.email) {
      emailSet.add(cse.email)
      labelSet.add(`${cse.label} - ${cse.email}`)
    }
    cse.contacts.forEach((contact) => {
      if (contact.canReceiveEmails && contact.email) {
        emailSet.add(contact.email)
        labelSet.add(
          `${toFullName(contact.firstName, contact.lastName, { options: { mode: 'lastname-firstname' } })} - ${contact.email}`
        )
      }
    })
  })

  return {
    emails: [...emailSet],
    labels: [...labelSet]
  }
}

export function CseListPage() {
  const { navigate, navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<CseTypes.Cse>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CseFilter>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformCseFiltersToUrl,
      transformUrlToFilter: transformUrlToCseFilters
    })

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'send-email' | 'filters',
    CseTypes.Cse[]
  >(['send-email', 'filters'])

  // Mock mutation for sending code to partners
  const { mutate: sendCodeToPartnersMutation } = useMutation({
    mutationFn: sendCodeToPartners,
    onSuccess: () => {
      toast.success('Le code a été envoyé aux partenaires avec succès')
      tableRef.current?.refetch()
    },
    onError: showAxiosError
  })

  // Mock mutation for sending email
  const { mutate: sendEmailMutation, isPending: isSendingEmail } = useMutation({
    mutationFn: sendEmailToCse,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const cseInformation = selectedData ? getCseInformation(selectedData) : undefined

  const columns: Column<CseTypes.Cse>[] = [
    {
      header: 'Identifiant',
      accessorKey: 'identifier'
    },
    {
      header: 'Libellé',
      accessorKey: 'label',
      enableSorting: true
    },
    {
      header: 'Ville',
      accessorKey: 'city',
      centerCell: true
    },
    {
      header: 'Responsable(s)',
      centerCell: true,
      accessorKey: 'managers',
      cell: ({ row }) => (
        <div className="text-balance text-center py-2">
          {row.original.contacts.length === 0 && <span>-</span>}
          {row.original.contacts
            .map((manager) =>
              toFullName(manager.firstName, manager.lastName, {
                options: { mode: 'lastname-firstname' }
              })
            )
            .join(', ')}
        </div>
      )
    },
    {
      header: 'Type de facturation',
      centerCell: true,
      accessorKey: 'billingType',
      cell: ({ row }) =>
        row.original.billingType ? billingTypeEnumMapping[row.original.billingType] : '-'
    },
    {
      header: 'Réduction(s)',
      accessorKey: 'reductionRules',
      centerCell: true,
      cell: ({ row }) => {
        return (
          <div className="grid grid-cols-2 gap-x-5">
            {row.original.reductionRules.length === 0 && <span>-</span>}
            {row.original.reductionRules.map((rule) => {
              const isPercentage = rule.type === ReductionTypeEnum.PERCENTAGE
              return (
                <Badge
                  key={rule.id}
                  text={isPercentage ? `${rule.reduction} %` : `${rule.reduction} €`}
                  className="mr-1 mb-1"
                />
              )
            })}
          </div>
        )
      }
    },
    {
      header: 'Inscrits N',
      centerCell: true,
      accessorKey: 'registrationYears',
      cell: ({ row }) => {
        const currentYear = new Date().getFullYear()
        const registrationYear = row.original.registrationYears.find(
          (ry) => ry.year === currentYear
        )
        return <span>{registrationYear?.numberOfRegistrations ?? 0}</span>
      }
    },
    {
      header: 'Inscrits N - 1',
      centerCell: true,
      accessorKey: 'registrationYears',
      cell: ({ row }) => {
        const currentYear = new Date().getFullYear()
        const registrationYear = row.original.registrationYears.find(
          (ry) => ry.year === currentYear - 1
        )
        return <span>{registrationYear?.numberOfRegistrations ?? 0}</span>
      }
    },
    {
      header: 'Inscrits N - 2',
      accessorKey: 'registrationYears',
      centerCell: true,
      cell: ({ row }) => {
        const currentYear = new Date().getFullYear()
        const registrationYear = row.original.registrationYears.find(
          (ry) => ry.year === currentYear - 2
        )
        return <span>{registrationYear?.numberOfRegistrations ?? 0}</span>
      }
    },
    {
      header: 'Status',
      centerCell: true,
      enableSorting: true,
      accessorKey: 'isActive',
      cell: ({ row }) => (
        <Badge
          variant={row.original.isActive ? 'success' : 'danger'}
          text={row.original.isActive ? 'Actif' : 'Inactif'}
        />
      )
    },
    {
      header: 'Nbre de salariés',
      enableSorting: true,
      centerCell: true,
      accessorKey: 'numberOfEmployees'
    },
    {
      header: 'Participation financière',
      enableSorting: true,
      accessorKey: 'councilAllowance',
      centerCell: true,
      cell: ({ row }) => (row.original.councilAllowance ? 'Oui' : 'Non')
    }
  ]

  return (
    <>
      <UserNavBar title={'CSE'} icon={ProviderIcon} />
      <div className="h-4" />

      <DynamicDataTable<CseTypes.Cse, CseFilter>
        onLoadingChange={setIsTableLoading}
        ref={tableRef}
        title={'Tous les CSE'}
        selectable
        columns={columns}
        endpoint={API.cse.getAll.url}
        allowSearchParam
        deleteMessage={
          'Vous êtes sur le point de supprimer un CSE. Cette action est irréversible.'
        }
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        onRowClick={(cse, event) =>
          navigateWithEvent(Paths.CSE_UPDATE_PAGE.replace(':id', cse.id.toString()), event)
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer un CSE',
            variant: 'primary',
            fn: () => navigate(Paths.CSE_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: SendIcon,
            label: 'Envoyer le code aux partenaires',
            variant: 'ghost',
            refetch: true,
            fn: (selected) => sendCodeToPartnersMutation(selected.map((s) => s.id))
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Envoyer un email',
            variant: 'ghost',
            fn: (selected) => toggleModal('send-email', selected)
          }
        ]}
      />

      {cseInformation && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails: cseInformation.emails,
              subject: '',
              body: ''
            }}
            labels={cseInformation.labels}
            buttonsConfig={modalButtonConfig({
              isLoading: isSendingEmail,
              onCancel: () => closeModel('send-email'),
              onConfirm: (values) =>
                sendEmailMutation({ ...values, emails: cseInformation.emails })
            })}
          />
        </Modal>
      )}

      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <CseFilters filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </>
  )
}
